/**
 * Reportes → Ventas por método de pago (`/reportes/ventas-por-metodo`).
 *
 * Puerto de las tres pantallas del panel anterior que compartían reporte:
 * *Reporte venta visa* (`Reportes::reporte_venta_visa_periodo` +
 * `get_comprobante_by_periodo_visa`), *… Izipay* y *… transferencia*
 * (`Reportes_model`), y sus vistas `reporte_venta_{visa,izipay,transferencia}_periodo.php`.
 *
 * La pantalla del panel anterior pedía los documentos del periodo al pulsar
 * *Generar* (con dos campos `datetime-local`) y los pintaba en una tabla con
 * exportaciones. Aquí el método se elige con pestañas (y viaja en la URL, así que
 * cada reporte del centro abre el suyo) y el rango de fechas se aplica al vuelo.
 *
 * Reglas conservadas:
 * - Se listan los documentos cobrados con ese método, tanto los del panel
 *   (**punto de venta**: factura o boleta, con el cobro principal o el secundario)
 *   como los del **APP** (`market_document_response`), con su comprobante, cliente,
 *   documento, importe, referencia, banco, turno y usuario.
 *
 * Diferencias anotadas: se excluyen las ventas anuladas y los pagos del APP se
 * identifican por el **tipo de método** (`payments[].payment_method_type_id`), no
 * por `payment_filename` (que en los datos viene vacío para VISA, así que el
 * reporte anterior no mostraba las visas del APP).
 *
 * Además del detalle, la pantalla ofrece la vista **por día**, que es la que
 * pedían *Reporte visa por mes* y *Reporte transferencia por mes*
 * (`Reportes::reporte_visa_diaria` + `metodo_generar_visa_diario` y
 * `…_transferencia_diaria`): un renglón por día con los documentos, el importe del
 * punto de venta, el del APP y el total, más los días con cobros y el día mayor.
 * Allí el día es el del **comprobante**, mientras que la pantalla anterior
 * agrupaba por el día del **turno** (una venta de madrugada caía en el día
 * anterior): se anota porque el total del mes no cambia, sólo el reparto diario.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatDateTimeValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useVentasPorMetodo,
  type FiltrosVentasPorMetodo,
  type MetodoDeVenta,
} from '../reportes.api';

/** Los tres métodos del reporte, con su etiqueta. */
const METODOS: { valor: MetodoDeVenta; etiqueta: string; descripcion: string }[] = [
  { valor: 'VISA', etiqueta: 'VISA', descripcion: 'Reporte venta visa' },
  { valor: 'IZIPAY', etiqueta: 'Izipay', descripcion: 'Reporte venta Izipay' },
  {
    valor: 'TRANSFERENCIA',
    etiqueta: 'Transferencia',
    descripcion: 'Reporte venta transferencia',
  },
];

/** Fecha local `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto: el mes en curso. */
function rangoPorDefecto() {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`,
    hasta: fechaLocal(hoy),
  };
}

const VISTAS: { valor: 'documentos' | 'dia'; etiqueta: string }[] = [
  { valor: 'documentos', etiqueta: 'Documentos' },
  { valor: 'dia', etiqueta: 'Por día' },
];

/** Vista que llega en la URL (`?vista=dia`), validada contra la lista. */
function vistaDeUrl(valor: string | null): 'documentos' | 'dia' {
  return valor === 'dia' ? 'dia' : 'documentos';
}

/** Método que llega en la URL (`?metodo=`), validado contra la lista. */
function metodoDeUrl(valor: string | null): MetodoDeVenta {
  const encontrado = METODOS.find((metodo) => metodo.valor === (valor ?? '').toUpperCase());

  return encontrado?.valor ?? 'VISA';
}

export function ReporteVentasPorMetodoPage() {
  useDocumentTitle('Reportes · Ventas por método de pago');

  const [parametros, setParametros] = useSearchParams();
  const [filtros, setFiltros] = useState<FiltrosVentasPorMetodo>(() => ({
    metodo: metodoDeUrl(parametros.get('metodo')),
    vista: vistaDeUrl(parametros.get('vista')),
    ...rangoPorDefecto(),
  }));
  const [aviso, setAviso] = useState<string | null>(null);

  // El método y la vista de la URL son los que fija el centro de reportes al
  // abrir el enlace.
  const metodoDeLaUrl = metodoDeUrl(parametros.get('metodo'));
  const vistaDeLaUrl = vistaDeUrl(parametros.get('vista'));

  useEffect(() => {
    setFiltros((actual) =>
      actual.metodo === metodoDeLaUrl && actual.vista === vistaDeLaUrl
        ? actual
        : { ...actual, metodo: metodoDeLaUrl, vista: vistaDeLaUrl },
    );
  }, [metodoDeLaUrl, vistaDeLaUrl]);

  const consulta = useVentasPorMetodo(filtros);
  const filas = consulta.data?.filas ?? [];
  // El listado del mes se pagina en el navegador; los repartos y el pie siguen usando el
  // listado completo.
  const paginacion = usePaginacion(filas, { clave: filtros });
  const porDia = consulta.data?.por_dia ?? [];
  const resumen = consulta.data?.resumen;
  const etiqueta = METODOS.find((metodo) => metodo.valor === filtros.metodo)?.etiqueta ?? '';

  // Días con cobros y día de mayor importe (las tarjetas de *Reporte visa por mes*).
  const diaMayor = porDia.reduce(
    (mayor, dia) => (dia.total > (mayor?.total ?? 0) ? dia : mayor),
    porDia[0],
  );

  const cambiarMetodo = (metodo: MetodoDeVenta) => {
    setFiltros((actual) => ({ ...actual, metodo }));
    setParametros(
      filtros.vista === 'dia' ? { metodo, vista: 'dia' } : { metodo },
      { replace: true },
    );
  };

  const cambiarVista = (vista: 'documentos' | 'dia') => {
    setFiltros((actual) => ({ ...actual, vista }));
    setParametros(
      vista === 'dia' ? { metodo: filtros.metodo, vista: 'dia' } : { metodo: filtros.metodo },
      { replace: true },
    );
  };

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarVentasPorMetodo(filtros), {
        nombre: `ventas-${filtros.metodo.toLowerCase()}-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const abrirPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfVentasPorMetodo(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  const tarjetas = [
    {
      etiqueta: 'Documentos',
      valor: formatNumber(resumen?.total_documentos ?? 0),
      detalle: `Promedio ${formatCurrency(resumen?.promedio ?? 0)}`,
    },
    {
      etiqueta: 'Total cobrado',
      valor: formatCurrency(resumen?.total_monto ?? 0),
      detalle: `${etiqueta} del periodo`,
    },
    {
      etiqueta: 'Punto de venta',
      valor: formatCurrency(resumen?.punto_de_venta ?? 0),
      detalle: 'Facturas y boletas del panel',
    },
    {
      etiqueta: 'APP',
      valor: formatCurrency(resumen?.app ?? 0),
      detalle: 'Documentos del punto de venta móvil',
    },
  ];

  const tarjetasDelDia = [
    {
      etiqueta: 'Días con cobros',
      valor: formatNumber(porDia.length),
      detalle: `${formatNumber(resumen?.total_documentos ?? 0)} documento(s)`,
    },
    {
      etiqueta: 'Total acumulado',
      valor: formatCurrency(resumen?.total_monto ?? 0),
      detalle: `promedio ${formatCurrency(
        porDia.length > 0 ? (resumen?.total_monto ?? 0) / porDia.length : 0,
      )} por día`,
    },
    {
      etiqueta: 'Día mayor',
      valor: formatCurrency(diaMayor?.total ?? 0),
      detalle: diaMayor ? formatDateValue(diaMayor.fecha) : 'sin cobros',
    },
    {
      etiqueta: 'Punto de venta',
      valor: formatCurrency(resumen?.punto_de_venta ?? 0),
      detalle: `APP ${formatCurrency(resumen?.app ?? 0)}`,
    },
  ];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Ventas por método de pago</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los documentos cobrados con VISA, Izipay o transferencia en el periodo, del panel y del
            APP.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={abrirPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
              Método
            </span>
            {METODOS.map((metodo) => (
              <button
                key={metodo.valor}
                type="button"
                aria-pressed={filtros.metodo === metodo.valor}
                onClick={() => cambiarMetodo(metodo.valor)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                  filtros.metodo === metodo.valor
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                )}
              >
                {metodo.etiqueta}
              </button>
            ))}
          </div>

          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) =>
                    setFiltros((actual) => ({
                      ...actual,
                      desde: evento.target.value || actual.desde,
                    }))
                  }
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) =>
                    setFiltros((actual) => ({
                      ...actual,
                      hasta: evento.target.value || actual.hasta,
                    }))
                  }
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Vista</span>
              <div className="inline-flex overflow-hidden rounded-control border border-ink-200">
                {VISTAS.map((opcion) => (
                  <button
                    key={opcion.valor}
                    type="button"
                    aria-pressed={filtros.vista === opcion.valor}
                    onClick={() => cambiarVista(opcion.valor)}
                    className={cn(
                      'h-11 px-3 text-xs font-semibold transition-colors',
                      filtros.vista === opcion.valor
                        ? 'bg-brand-50 text-brand-700'
                        : 'bg-white text-ink-600 hover:text-brand-700',
                    )}
                  >
                    {opcion.etiqueta}
                  </button>
                ))}
              </div>
            </label>

            <div className="flex flex-wrap items-end gap-2 sm:col-span-2">
              <Button
                variant="outline"
                onClick={() =>
                  setFiltros((actual) => ({ ...actual, ...rangoPorDefecto() }))
                }
                leftIcon={<Search className="size-4" aria-hidden="true" />}
              >
                Este mes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const hasta = new Date();
                  const desde = new Date();

                  desde.setDate(desde.getDate() - 30);

                  setFiltros((actual) => ({
                    ...actual,
                    desde: fechaLocal(desde),
                    hasta: fechaLocal(hasta),
                  }));
                }}
              >
                Últimos 30 días
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron cargar las ventas del método">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="warning" title="Revisa el dato">
          {aviso}
        </Alert>
      ) : null}

      {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

      {!consulta.isLoading ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {(filtros.vista === 'dia' ? tarjetasDelDia : tarjetas).map((tarjeta) => (
              <Card key={tarjeta.etiqueta}>
                <CardContent className="p-4">
                  <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    {tarjeta.etiqueta}
                  </p>
                  <p className="mt-1 text-xl font-bold text-ink-900">{tarjeta.valor}</p>
                  <p className="mt-0.5 text-xs text-ink-500">{tarjeta.detalle}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {filtros.vista === 'dia' ? (
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-3">
                  <p className="text-sm font-semibold text-ink-800">
                    {etiqueta} por día
                  </p>
                  <Badge variant="brand">{formatNumber(porDia.length)} día(s)</Badge>
                </div>

                <div className="max-h-[34rem] overflow-auto">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="sticky top-0 bg-ink-50">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-3 py-2">Día</th>
                        <th className="px-3 py-2 text-right">Documentos</th>
                        <th className="px-3 py-2 text-right">Punto de venta</th>
                        <th className="px-3 py-2 text-right">APP</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {porDia.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-10 text-center text-sm text-ink-500">
                            No hay cobros con {etiqueta} en el periodo.
                          </td>
                        </tr>
                      ) : null}

                      {porDia.map((dia) => (
                        <tr
                          key={dia.fecha}
                          className={cn(
                            'border-t border-ink-100',
                            dia.fecha === diaMayor?.fecha && 'bg-brand-50/40',
                          )}
                        >
                          <td className="px-3 py-2 font-semibold text-ink-900">
                            {formatDateValue(dia.fecha)}
                          </td>
                          <td className="px-3 py-2 text-right text-ink-600">
                            {formatNumber(dia.documentos)}
                          </td>
                          <td className="px-3 py-2 text-right text-ink-700">
                            {formatCurrency(dia.punto_de_venta)}
                          </td>
                          <td className="px-3 py-2 text-right text-ink-700">
                            {formatCurrency(dia.app)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-ink-900">
                            {formatCurrency(dia.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {porDia.length > 0 ? (
                      <tfoot className="bg-ink-50 font-semibold">
                        <tr className="border-t-2 border-ink-200">
                          <td className="px-3 py-2">
                            TOTALES · {formatNumber(porDia.length)} día(s)
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatNumber(resumen?.total_documentos ?? 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(resumen?.punto_de_venta ?? 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(resumen?.app ?? 0)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatCurrency(resumen?.total_monto ?? 0)}
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : filas.length === 0 ? (
            <Alert variant="info" title={`Sin cobros con ${etiqueta} en el periodo`}>
              Prueba con otro rango de fechas. El método de una venta se puede corregir desde la
              liquidación del turno (botón *Métodos de pago*).
            </Alert>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-3">
                  <p className="text-sm font-semibold text-ink-800">
                    Documentos cobrados con {etiqueta}
                  </p>
                  <Badge variant="brand">{formatNumber(filas.length)} fila(s)</Badge>
                </div>

                <div className="max-h-[34rem] overflow-auto">
                  <table className="w-full border-collapse text-[13px]">
                    <thead className="sticky top-0 bg-ink-50">
                      <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                        <th className="px-3 py-2">Comprobante</th>
                        <th className="px-3 py-2">Tipo</th>
                        <th className="px-3 py-2">Fecha</th>
                        <th className="px-3 py-2">Cliente</th>
                        <th className="px-3 py-2 text-right">Monto</th>
                        <th className="px-3 py-2">Referencia</th>
                        <th className="px-3 py-2">Banco</th>
                        <th className="px-3 py-2">Turno</th>
                        <th className="px-3 py-2">Usuario</th>
                        <th className="px-3 py-2">Origen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginacion.visibles.map((fila, indice) => (
                        <tr
                          key={`${fila.numero_comprobante}-${fila.tipo_comprobante}-${indice}`}
                          className="border-t border-ink-100"
                        >
                          <td className="px-3 py-2 font-semibold text-ink-900">
                            {fila.numero_comprobante}
                          </td>
                          <td className="px-3 py-2 text-xs text-ink-600">{fila.tipo_comprobante}</td>
                          <td className="px-3 py-2 text-xs text-ink-500">
                            {formatDateTimeValue(fila.fecha)}
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-ink-700">{fila.cliente || '—'}</span>
                            {fila.documento ? (
                              <span className="block text-[11px] text-ink-400">
                                {fila.documento}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-ink-900">
                            {formatCurrency(fila.monto)}
                          </td>
                          <td className="px-3 py-2 text-ink-600">{fila.referencia || '—'}</td>
                          <td className="px-3 py-2 text-xs text-ink-500">{fila.banco || '—'}</td>
                          <td className="px-3 py-2 text-xs text-ink-500">
                            {fila.turno_id ? `#${fila.turno_id}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-ink-600">{fila.usuario || '—'}</td>
                          <td className="px-3 py-2">
                            <Badge variant={fila.origen === 'MARKET' ? 'success' : 'outline'}>
                              {fila.origen}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-ink-50 font-semibold">
                      <tr className="border-t-2 border-ink-200">
                        <td className="px-3 py-2" colSpan={4}>
                          TOTALES · {formatNumber(resumen?.total_documentos ?? 0)} documento(s)
                        </td>
                        <td className="px-3 py-2 text-right">
                          {formatCurrency(resumen?.total_monto ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-xs" colSpan={2}>
                          Punto de venta {formatCurrency(resumen?.punto_de_venta ?? 0)}
                        </td>
                        <td className="px-3 py-2 text-xs" colSpan={2}>
                          APP {formatCurrency(resumen?.app ?? 0)}
                        </td>
                        <td className="px-3 py-2" />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <Paginador {...paginacion} etiqueta="ventas" />
              </CardContent>
            </Card>
          )}
        </>
      ) : null}

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        <span>
          Una venta puede ir repartida entre dos métodos: si el método del reporte es el secundario,
          la fila trae el importe de esa parte del cobro. Las ventas anuladas no se listan y los pagos
          del APP se identifican por el tipo de método del documento. En la vista{' '}
          <strong>por día</strong> el día es el del comprobante (el panel anterior agrupaba por el día
          del turno, así que una venta de madrugada caía en el día anterior); el total del periodo no
          cambia.
        </span>
      </p>
    </div>
  );
}
